"use client";
import {usePathname, useRouter} from "next/navigation";
import Link from "next/link";
import {useCallback, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowRight, faArrowLeft} from "@fortawesome/free-solid-svg-icons";
import {useSidebar} from "./SidebarContext";

import ArrowUpRightIcon from "../ArrowUpRightIcon";
import {useSession, signOut} from "next-auth/react";
import {getAuth, signOut as firebaseSignOut} from "firebase/auth";
import LoadingOverlay from "../LoadingOverlay";
import ProfileBtn from "../ProfileBtn";
import LocaleLink from "../LocaleLink";
import {withAutoLang} from "@/app/utils/localeUtil";
import {Locale} from "@/i18n-config";

/* ---------------- NAV CONFIG ---------------- */ const PUBLIC_NAV_PRODUCTS = [
  // {type: "heading", en_label: "Products", ru_label: "Products"},
  {
    type: "group",
    en_label: "Youtube",
    ru_label: "Youtube",
    items: [
      {type: "link", href: "/youtube/interviews", en_label: "Interviews", ru_label: "Интервью"},
      {type: "link", href: "/youtube/podcasts", en_label: "Podcasts", ru_label: "Подкасты"},
      {type: "link", href: "/youtube/courses", en_label: "Courses", ru_label: "Курсы"},
      {type: "link", href: "/youtube/episodes", en_label: "Episodes", ru_label: "Выпуски"},
      {type: "link", href: "/youtube/mind-blowing-ai", en_label: "Mind Blowing AI", ru_label: "УДИВИТЕЛЬНЫЙ AI"},
      {type: "link", href: "/youtube/how-not-to-go-bankrupt", en_label: "Audiobook", ru_label: "Аудиокнига"},
      {type: "link", href: "/youtube/ai-kids", en_label: "AI Kids", ru_label: "Дети AI"}
    ]
  },
  {
    type: "group",
    en_label: "Company",
    ru_label: "Компания",
    items: [
      {type: "link", href: "/services", en_label: "AI Services", ru_label: "AI Услуги"},
      {type: "link", href: "/about", en_label: "About Us", ru_label: "О нас"},
      {type: "link", href: "/contact", en_label: "Contacts", ru_label: "Контакты"},
      {type: "link", href: "/careers", en_label: "Careers", ru_label: "Карьера"}
    ]
  },
  // {
  //   type: "group",
  //   en_label: "For Investors",
  //   ru_label: "Инвесторам",
  //   items: [
  //     // {href: "/for-investors", en_label: "Review", ru_label: "Обзор"},
  //     // {href: "/our-investments", en_label: "Our Focus", ru_label: "Наш фокус"},
  //     {type: "link", href: "/pitch-deck", en_label: "Pitch Deck", ru_label: "Презентация"}
  //   ]
  // },
  {
    type: "group",
    en_label: "For kids",
    ru_label: "Детям",
    items: [{type: "link", href: "/quantum-kingdom", en_label: "Our Impact", ru_label: "Наш вклад"}]
  },
  {
    type: "group",
    en_label: "For Partners",
    ru_label: "Партнерам",
    items: [
      {type: "link", href: "/partners", en_label: "Trusted Partners", ru_label: "Наши партнеры"},
      {type: "link", href: "/partnership", en_label: "Partnership", ru_label: "Сотрудничество"}
    ]
  }
] as const;

const PUBLIC_NAV_MOZGII = [] as const;

/** Extra menu that appears **only** for logged-in users */
const ACCOUNT_NAV = [
  {type: "heading", en_label: "MOZG AI", ru_label: "MOZG AI"},
  {type: "link", href: "/profile", en_label: "Profile", ru_label: "Профиль"},
  {type: "link", href: "/profile/settings", en_label: "Settings", ru_label: "Настройки"},
  {type: "link", href: "/profile/mozg-units", en_label: "Wallet", ru_label: "Кошелек"},
  {type: "action", en_label: "Sign Out", ru_label: "Выйти"}
] as const;

export function useActiveChecker() {
  const pathname = usePathname();
  const [currentAnchor, setCurrentAnchor] = useState<string | null>(null);

  /* remember the last clicked #anchor so hash-links highlight correctly */
  const onNavClick = useCallback((href: string) => {
    const [, anchor] = href.split("#");
    setCurrentAnchor(anchor ?? null);
  }, []);

  const isActive = useCallback(
    (href: string) => {
      if (href.startsWith("http")) return false; // external link → never active

      const [hrefPath, anchor] = href.split("#");

      const normalizedHrefPath = hrefPath.replace(/\/$/, "");
      const normalizedPathname = pathname?.replace(/\/$/, "");

      if (anchor) {
        const pathOK =
          hrefPath === "/" // root: exact match only
            ? pathname === "/"
            : pathname === normalizedHrefPath || pathname?.startsWith(`${normalizedHrefPath}/`);
        // console.log("pathOK", pathOK);
        // console.log("href", href);
        // console.log("anchor", anchor);
        // console.log("normalizedHrefPath", normalizedHrefPath);
        // console.log("hrefPath", hrefPath);
        // console.log("pathname", pathname);
        if (!pathOK) return false;
      } else {
        const pathOK =
          hrefPath === "/" // root: exact match only
            ? pathname === "/"
            : normalizedPathname === hrefPath;
        // console.log("pathOK", pathOK);
        // console.log("href", href);
        // console.log("normalizedHrefPath", normalizedHrefPath);
        // console.log("hrefPath", hrefPath);
        // console.log("pathname", pathname);
        if (!pathOK) return false;
      }

      /* hash-link logic: active when its anchor is the one we just clicked */
      return anchor ? currentAnchor === anchor : currentAnchor === null;
    },
    [pathname, currentAnchor]
  );

  return {isActive, onNavClick};
}

function RenderLink({href, label, active, onClick}: {href: string; label: string; active: boolean; onClick?: () => void}) {
  const base = "block px-3xs py-2xs rounded-md transition-colors uppercase flex items-center gap-1";
  const color = active ? "text-gold hover:text-gray-500" : "hover:text-gold";
  const isExternal = href.startsWith("http");

  return (
    <LocaleLink href={href} target={isExternal ? "_blank" : undefined} rel={isExternal ? "noopener noreferrer" : undefined} onClick={onClick} className={`${base} ${color}`}>
      {label}
      {isExternal && <ArrowUpRightIcon className="inline  ml-1 mb-2" width={14} height={14} aria-hidden="true" />}
    </LocaleLink>
  );
}

/* ---------------- COMPONENT ---------------- */
export default function NavLeftSidebar({locale}: {locale: Locale}) {
  const {open, mobileOpen, closeMobile} = useSidebar();
  const router = useRouter();

  const {isActive, onNavClick} = useActiveChecker();
  const {data: session, status} = useSession(); // ← v5
  const navData = session?.user ? [...PUBLIC_NAV_PRODUCTS, ...ACCOUNT_NAV] : [...PUBLIC_NAV_PRODUCTS, ...PUBLIC_NAV_MOZGII];
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSignOut = async () => {
    try {
      setIsLoading(true);
      console.log("Starting sign out process...");

      const result = await signOut({redirect: false});
      console.log("NextAuth sign out completed. Result:", result);

      const authObj = getAuth();
      await firebaseSignOut(authObj);

      router.push("/");
      // Redirect to homepage.
    } catch (error) {
      console.error("Sign out error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const Level1 = (onLinkClick: () => void) => (
    <ul className="space-y-4 text-2xl font-inter-display font-bold uppercase">
      {navData.map((entry) => {
        const label = locale === "ru" ? entry.ru_label : entry.en_label;
        /* ───────── SECTION TITLE ───────── */
        if (entry.type === "heading") {
          return (
            <li key={`heading-${label}`} className="pt-6">
              <span className="block px-3xs text-2xl tracking-wide text-gray-500 font-semibold">{label}</span>
            </li>
          );
        }

        /* ───────── SINGLE LINK ───────── */
        if (entry.type === "link") {
          return (
            <li key={entry.href}>
              <RenderLink
                href={entry.href}
                label={label}
                onClick={() => {
                  onNavClick(entry.href);
                  onLinkClick();
                }}
                active={isActive(entry.href)}
              />
            </li>
          );
        }
        if (entry.type === "action") {
          return (
            <li key="signout">
              <button
                onClick={() => {
                  onLinkClick?.(); // close sidebar (mobile)
                  handleSignOut();
                }}
                className="px-3xs py-2xs rounded-md transition-colors uppercase flex items-center gap-1 hover:text-gold">
                {label}
              </button>
            </li>
          );
        }

        /* ───────── COLLAPSIBLE GROUP ───────── */
        const groupLabel = locale === "ru" ? entry.ru_label : entry.en_label;
        return (
          <li key={groupLabel}>
            <button
              onClick={() => {
                setActiveGroup(entry.items[0].href); // Use a unique identifier like href
                router.push(withAutoLang(entry.items[0].href)); // navigate to the first link in the group
                onNavClick(entry.items[0].href);
                onLinkClick();
              }}
              className="group flex w-full items-center justify-between cursor-pointer uppercase px-3xs py-2xs rounded-md hover:text-gold transition-colors">
              <span>{groupLabel}</span>
              <FontAwesomeIcon icon={faArrowRight} size="xs" className="opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          </li>
        );
      })}
    </ul>
  );

  const Level2 = (entry: Extract<(typeof navData)[number], {type: "group"}>, onLinkClick: () => void) => {
    const groupLabel = locale === "ru" ? entry.ru_label : entry.en_label;
    return (
      <ul className="space-y-4 text-2xl font-inter-display font-bold uppercase">
        <li className="">
          <button
            onClick={() => setActiveGroup(null)}
            className="flex w-full button-arrow  text-xl hover:wiggle items-center gap-2  py-2xs px-3xs uppercase rounded-md text-gray-500 hover:text-gold transition-colors">
            <FontAwesomeIcon icon={faArrowLeft} size="xs" className="arrow" />
            <span>{groupLabel}</span>
          </button>
        </li>
        {entry.items.map((item) => {
          const itemLabel = locale === "ru" ? item.ru_label : item.en_label;
          if (item.type === "link") {
            return (
              <li key={item.href}>
                <RenderLink
                  href={item.href}
                  label={itemLabel}
                  onClick={() => {
                    onNavClick(item.href);
                    onLinkClick();
                  }}
                  active={isActive(item.href)}
                />
              </li>
            );
          }
          return null;
        })}
      </ul>
    );
  };

  /* ---------------- DESKTOP (md+) ---------------- */
  const DesktopSidebar = (
    <aside
      id="navigation-sidebar"
      className={`hidden md:block  md:overflow-visible  ${open ? "fixed " : "fixed"} left-0 top-header-h md:top-0 w-full md:min-w-[14rem] transition-transform duration-300 ease-in-out ${
        open ? "translate-x-0" : "-translate-x-full"
      }`}>
      <div className="absolute inset-0 ">
        <div className="fixed left-0 top-[287px] h-[calc(100svh-287px)] min-w-[11rem] select-none  pr-4">
          <div className="relative h-full ">
            {/* Level 1 */}
            <div className={`absolute inset-0 min-w-[11rem] transition-transform duration-300 pl-8 ease-in-out ${activeGroup ? "-translate-x-full" : "translate-x-0"}`}>{Level1(() => {})}</div>

            {/* Level 2 */}
            {activeGroup &&
              (() => {
                const entry = navData.find((e): e is Extract<(typeof navData)[number], {type: "group"}> => e.type === "group" && e.items.some((item) => item.href === activeGroup));
                if (!entry) return null;
                return (
                  <div
                    className={`
                    absolute inset-0 w-[11rem] overflow-y-auto overflow-x-visible pl-8
                      /* ← start off-canvas */
                    ${activeGroup ? "animate-[slide-in-right_300ms_ease-in-out_forwards]" : ""}
                  `}>
                    {Level2(entry, () => {})}
                  </div>
                );
              })()}
          </div>
        </div>
      </div>
    </aside>
  );

  /* ---------------- MOBILE (<768px) ---------------- */
  const MobileSidebar = (
    <>
      <div
        aria-hidden="true"
        className={`fixed left-0 mt-header-h  h-[calc(100svh-var(--header-h))]  w-full bg-black/20  md:hidden backdrop-blur-md  transition-opacity duration-300 ${
          mobileOpen ? "opacity-80" : "opacity-0 pointer-events-none"
        }`}></div>
      <aside
        className={`fixed mt-header-h  h-[calc(100svh-var(--header-h))]  top-0 left-0 w-[90vw] z-50  bg-black md:hidden transition-transform duration-300 ease-in-out ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}>
        <div className="flex h-full flex-col   overflow-y-auto px-8 pt-24 pb-10">
          {/* Level 1 */}

          <div className="flex-1 max-w-[11rem]">
            {!activeGroup && Level1(closeMobile)}
            {activeGroup &&
              (() => {
                const entry = navData.find((e): e is Extract<(typeof navData)[number], {type: "group"}> => e.type === "group" && e.items.some((item) => item.href === activeGroup));
                if (!entry) return null;
                return Level2(entry, closeMobile);
              })()}
          </div>
          <div className="flex justify-end">
            {status === "loading" ? (
              <svg className="h-12 w-14 animate-pulse text-gray-300" viewBox="0 0 320 110" fill="currentColor">
                <rect x="20" y="7" width="320" height="110" rx="4" />
              </svg>
            ) : !session?.user ? (
              <Link href="/login" onClick={closeMobile} className="rounded-full bg-gray-800 px-6 pt-3 pb-2 text-sm text-white transition-all duration-300 hover:bg-black hover:text-gold">
                Log In
              </Link>
            ) : (
              <ProfileBtn onClick={closeMobile} />
            )}
            {/* <Link href="/login" onClick={closeMobile} className="rounded-full bg-gray-800 px-6 pt-3 pb-2 text-sm text-white transition-all duration-300 hover:bg-black hover:text-gold">
              Log In
            </Link> */}
          </div>
        </div>
      </aside>
    </>
  );

  return (
    <>
      {isLoading && <LoadingOverlay />}
      {DesktopSidebar}
      {MobileSidebar}
    </>
  );
}
